import { isDevEnv } from "@/config/env"
import { faker } from "@faker-js/faker"
import { zodResolver } from "@hookform/resolvers/zod"
import { httpsCallable } from "firebase/functions"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { InputFormControl } from "../../components/form/InputFormControl"
import { useGlobalSettings } from "@/entities/globalSettings/useGlobalSettings"
import { useProcess } from "../../contexts/ProcessContext"
import { emailSchema, firstNameSchema, lastNameSchema, phoneSchema } from "../../entities/user/user"
import { getConfiguredFunctions } from "../../firebase/configuredFunctions"
import { uploadPhoto } from "../../firebase/storage"
import { optionalString } from "../../utils/validation/optionalString"
import { icon_outline_plus } from "@/utils/svgIcons"

const addUserSchema = z.object({
   email: emailSchema,
   password: optionalString(
      z
         .string()
         .min(8, "Le mot de passe doit contenir au moins 8 caractères")
         .regex(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/,
            "Le mot de passe doit contenir au moins une lettre minuscule, une lettre majuscule et un chiffre",
         ),
   ),
   firstName: firstNameSchema,
   lastName: lastNameSchema,
   phone: optionalString(phoneSchema),
   contactsEmails: z.array(optionalString(emailSchema)).default([]),
   contactsPhones: z.array(optionalString(phoneSchema)).default([]),
   role: z.enum(["admin", "young"]),
   group: z.string(),
   notificationPreferences: z
      .object({
         email: z.boolean(),
         sms: z.boolean(),
      })
      .optional()
      .default({
         email: true,
         sms: true,
      }),
   photoFiles: z.instanceof(FileList).optional(),
})

type FormData = z.infer<typeof addUserSchema>

export default function AddUser() {
   const {
      register,
      handleSubmit,
      formState: { errors },
      watch,
      reset,
   } = useForm<FormData>({
      resolver: zodResolver(addUserSchema),
      mode: "onBlur",
      defaultValues: {
         email: "",
         firstName: "",
         lastName: "",
         phone: "",
         role: "young",
         group: "",
         contactsEmails: [],
         contactsPhones: [],
         notificationPreferences: {
            email: true,
            sms: true,
         },
         password: isDevEnv ? "Secret123" : "",
         ...(isDevEnv && {
            email: `test-${Date.now()}@example.com`,
            firstName: faker.person.firstName(),
            lastName: faker.person.lastName(),
            phone: "0601010101",
            contactsEmails: ["contact1@example.com"],
            contactsPhones: ["0601010101"],
         }),
      },
   })

   const values = watch()
   const { startProcess } = useProcess()
   const globalSettingsQuery = useGlobalSettings()

   const onSubmit = async (data: FormData) => {
      startProcess(() => createUser(data))
   }

   const createUser = async (data: FormData) => {

      let photoUrl = ""
      if (data.photoFiles?.[0]) {
         photoUrl = await uploadPhoto(data.photoFiles[0])
      }

      delete data.photoFiles

      data.contactsEmails = data.contactsEmails
         .map((email) => email.trim())
         .filter((email) => Boolean(email))
      data.contactsPhones = data.contactsPhones
         .map((phone) => phone.trim())
         .filter((phone) => Boolean(phone))

      const createUser = httpsCallable(getConfiguredFunctions(), "adminCreateUser")
      await createUser({
         ...data,
         photo: photoUrl,
         redirectUrl: "/",
      })

      reset()
   }

   return (
      <div className="page-section max-w-xl">
         <h2 className="title">Ajouter un utilisateur</h2>

         <form onSubmit={handleSubmit(onSubmit)} className="">
            <div className="space-y-4">
               <InputFormControl
                  label="Email"
                  field="email"
                  type="email"
                  register={register}
                  errors={errors}
               />

               {isDevEnv && (
                  <InputFormControl
                     label="Développement: Mot de passe temporaire"
                     field="password"
                     type="text"
                     register={register}
                     errors={errors}
                     disabled
                  />
               )}

               <InputFormControl
                  label="Prénom"
                  field="firstName"
                  register={register}
                  errors={errors}
               />

               <InputFormControl label="Nom" field="lastName" register={register} errors={errors} />

               <InputFormControl
                  label="Téléphone"
                  field="phone"
                  type="tel"
                  register={register}
                  errors={errors}
               />

               <div className="form-control">
                  <label className="label">
                     <span className="label-text">Photo</span>
                  </label>
                  <input
                     type="file"
                     className="file-input file-input-bordered w-full"
                     accept="image/*"
                     {...register("photoFiles")}
                  />
                  {errors.photoFiles && (
                     <div className="text-error mt-1">{errors.photoFiles.message}</div>
                  )}
               </div>

               <div className="form-control">
                  <label className="label">
                     <span className="label-text">Rôle</span>
                  </label>
                  <select className="select select-bordered w-full" {...register("role")}>
                     <option value="young">Jeune</option>
                     <option value="admin">Admin</option>
                  </select>
               </div>

               {values.role === "young" && (
                  <>
                     {/* TODO use InputFormControl ? */}
                     <div className="form-control">
                        <label className="label">
                           <span className="label-text">Groupe</span>
                        </label>
                        <select className="select select-bordered w-full" {...register("group")}>
                           {globalSettingsQuery.data?.groups.map((group) => (
                              <option key={group} value={group}>
                                 {group}
                              </option>
                           ))}
                        </select>
                     </div>

                     <div className="divider mt-12 mb-6 pt-6">Contacts</div>
                     <div className="space-y-4">
                        <div>
                           <label className="label">
                              <span className="label-text">Emails de contact</span>
                           </label>
                           {values.contactsEmails?.map((_, index) => (
                              <div key={index} className="flex items-center gap-2 mb-2">
                                 <InputFormControl
                                    label=""
                                    register={register}
                                    field={`contactsEmails.${index}`}
                                    type="email"
                                    errors={errors}
                                    className="flex-1"
                                 />
                                 <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => {
                                       const newEmails = [...values.contactsEmails]
                                       newEmails.splice(index, 1)
                                       reset({ ...values, contactsEmails: newEmails })
                                    }}
                                 >
                                    <svg
                                       xmlns="http://www.w3.org/2000/svg"
                                       fill="none"
                                       viewBox="0 0 24 24"
                                       strokeWidth={1.5}
                                       stroke="currentColor"
                                       className="w-5 h-5"
                                    >
                                       <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M6 18L18 6M6 6l12 12"
                                       />
                                    </svg>
                                 </button>
                              </div>
                           ))}
                           <button
                              type="button"
                              className="btn _btn-ghost btn-sm mt-2"
                              onClick={() => {
                                 reset({
                                    ...values,
                                    contactsEmails: [...(values.contactsEmails || []), ""],
                                 })
                              }}
                           >
                              + Ajouter un email
                           </button>
                        </div>

                        <div>
                           <label className="label">
                              <span className="label-text">Téléphones de contact</span>
                           </label>
                           {values.contactsPhones?.map((_, index) => (
                              <div key={index} className="flex items-center gap-2 mb-2">
                                 <InputFormControl
                                    label=""
                                    register={register}
                                    field={`contactsPhones.${index}`}
                                    type="tel"
                                    errors={errors}
                                    className="flex-1"
                                 />
                                 <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => {
                                       const newPhones = [...values.contactsPhones]
                                       newPhones.splice(index, 1)
                                       reset({ ...values, contactsPhones: newPhones })
                                    }}
                                 >
                                    <svg
                                       xmlns="http://www.w3.org/2000/svg"
                                       fill="none"
                                       viewBox="0 0 24 24"
                                       strokeWidth={1.5}
                                       stroke="currentColor"
                                       className="w-5 h-5"
                                    >
                                       <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          d="M6 18L18 6M6 6l12 12"
                                       />
                                    </svg>
                                 </button>
                              </div>
                           ))}
                           <button
                              type="button"
                              className="btn _btn-ghost btn-sm mt-2"
                              onClick={() => {
                                 reset({
                                    ...values,
                                    contactsPhones: [...(values.contactsPhones || []), ""],
                                 })
                              }}
                           >
                              + Ajouter un téléphone
                           </button>
                        </div>
                     </div>
                  </>
               )}

               {values.role === "admin" && (
                  <div className="space-y-4">
                     <div className="form-control">
                        <label className="label cursor-pointer">
                           <span className="label-text">Notifications par email</span>
                           <input
                              type="checkbox"
                              className="toggle"
                              {...register("notificationPreferences.email")}
                           />
                        </label>
                     </div>
                     <div className="form-control">
                        <label className="label cursor-pointer">
                           <span className="label-text">Notifications par SMS</span>
                           <input
                              type="checkbox"
                              className="toggle"
                              {...register("notificationPreferences.sms")}
                           />
                        </label>
                     </div>
                  </div>
               )}
            </div>

            <div className="mt-12 flex justify-center">
               <button type="submit" className="btn btn-primary">
                  {icon_outline_plus}
                  Créer l'utilisateur et envoyer l'invitation
               </button>
            </div>
         </form>
      </div>
   )
}
